import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material';
// import type { SelectChangeEvent } from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { AppUser, UserRole } from '../../types/user';
import toast from 'react-hot-toast';

// Interface pour gérer les timestamps Firestore et les dates
type FirestoreTimestamp = {
  toDate: () => Date;
} & Date;

type UserWithTimestamp = Omit<ExtendedAppUser, 'createdAt'> & {
  id: string;
  createdAt: Date | FirestoreTimestamp | string | null | undefined;
};

// Étendre l'interface AppUser pour inclure un statut
interface ExtendedAppUser extends AppUser {
  status: 'active' | 'suspended';
  id: string;
}

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [users, setUsers] = useState<UserWithTimestamp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  // États pour les dialogues
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithTimestamp | null>(null);
  const [editedRole, setEditedRole] = useState<UserRole>('coach');
  const [editedStatus, setEditedStatus] = useState<'active' | 'suspended'>('active');

  // Charger les utilisateurs depuis Firestore
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        // Par défaut, tous les utilisateurs sont considérés comme actifs
        // sauf si un champ status existe déjà
        return {
          ...data,
          id: doc.id,
          status: (data.status || 'active') as 'active' | 'suspended',
          createdAt: data.createdAt || null
        } as UserWithTimestamp;
      });
      setUsers(usersList);
      setError(null);
    } catch (err: any) {
      setError(err.message);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filtrer les utilisateurs en fonction des critères de recherche et de filtre
  const filteredUsers = users.filter((user: UserWithTimestamp) => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fonction pour supprimer un utilisateur
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await deleteDoc(doc(db, 'users', selectedUser.id));
      setUsers(users.filter(u => u.id !== selectedUser.id));
      toast.success('Utilisateur supprimé avec succès');
      handleCloseDeleteDialog();
    } catch (err: any) {
      toast.error(`Erreur lors de la suppression: ${err.message}`);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenDeleteDialog = (user: UserWithTimestamp) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Gestion des dialogues
  const handleOpenEditDialog = (user: UserWithTimestamp) => {
    setSelectedUser(user);
    setEditedRole(user.role);
    setEditedStatus(user.status);
    setEditDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  // Actions sur les utilisateurs
  const formatDate = (date: Date | FirestoreTimestamp | string | null | undefined): string => {
    if (!date) return '-';
    
    try {
      // Vérifier si c'est un objet Firestore Timestamp
      if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
        return date.toDate().toLocaleDateString();
      }
      // Si c'est une chaîne ou une date
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj instanceof Date ? dateObj.toLocaleDateString() : '-';
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return '-';
    }
  };

  // Cette fonction n'est plus utilisée, remplacée par handleUpdateUser
  /*
  const handleSaveChanges = async () => {
    if (!selectedUser) return;
    
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        role: editedRole,
        status: editedStatus,
        updatedAt: new Date()
      });
      
      // Mettre à jour l'état local
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, role: editedRole, status: editedStatus } 
          : user
      ));
      
      toast.success(`L'utilisateur ${selectedUser.email} a été mis à jour`);
    } catch (err: any) {
      setError(err.message);
      toast.error('Erreur lors de la mise à jour de l\'utilisateur');
    }
  };
  */

  // Fonction pour mettre à jour le statut d'un utilisateur
  const updateUserStatus = async (userId: string, newStatus: 'active' | 'suspended') => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: newStatus
      });
      
      // Mettre à jour l'état local
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, status: newStatus } : user
        )
      );
      
      return true;
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      toast.error(`Erreur lors de la mise à jour du statut: ${err.message}`);
      return false;
    }
  };
  
  // Fonction pour mettre à jour le rôle et le statut d'un utilisateur
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        role: editedRole,
        status: editedStatus
      });
      
      // Mettre à jour l'état local
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === selectedUser.id 
            ? { ...user, role: editedRole, status: editedStatus } 
            : user
        )
      );
      
      toast.success(`Utilisateur mis à jour avec succès`);
      handleCloseEditDialog();
    } catch (err: any) {
      console.error("Erreur lors de la mise à jour de l'utilisateur:", err);
      toast.error(`Erreur lors de la mise à jour: ${err.message}`);
    }
  };

  // Fonction pour basculer le statut d'un utilisateur (actif/suspendu)
  const handleToggleUserStatus = async (user: UserWithTimestamp) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      await updateUserStatus(user.id, newStatus);
      toast.success(`L'utilisateur a été ${newStatus === 'active' ? 'activé' : 'suspendu'} avec succès`);
    } catch (err) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  return (
    <Box className="px-2 sm:px-0">
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: isMobile ? 2 : 3,
        gap: isMobile ? 2 : 0
      }}>
        <Typography variant="h6" component="h2" className="text-blue-800 font-bold">
          Gestion des utilisateurs
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          fullWidth={isMobile}
          sx={{
            borderRadius: '8px',
            padding: isMobile ? '10px 16px' : '8px 16px',
            fontSize: isMobile ? '0.95rem' : '0.875rem',
            height: isMobile ? '48px' : 'auto',
            textTransform: 'none'
          }}
        >
          Actualiser
        </Button>
      </Box>

      {/* Filtres et recherche */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 2, 
        mb: 3, 
        flexWrap: isMobile ? 'nowrap' : 'wrap' 
      }}>
        <TextField
          label="Rechercher"
          variant="outlined"
          size={isMobile ? "medium" : "small"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ 
            minWidth: isMobile ? '100%' : 200, 
            flex: isMobile ? 'none' : 1,
            '& .MuiInputBase-root': { 
              fontSize: isMobile ? '1rem' : 'inherit',
              height: isMobile ? '56px' : 'inherit'
            } 
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'row' : 'row',
          gap: 2,
          width: isMobile ? '100%' : 'auto'
        }}>
          <FormControl 
            size={isMobile ? "medium" : "small"} 
            sx={{ 
              minWidth: isMobile ? '50%' : 120,
              flex: isMobile ? 1 : 'none'
            }}
          >
            <InputLabel>Rôle</InputLabel>
            <Select
              value={filterRole}
              label="Rôle"
              onChange={(e) => setFilterRole(e.target.value)}
              sx={{ 
                fontSize: isMobile ? '1rem' : 'inherit',
                height: isMobile ? '56px' : 'inherit'
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: isMobile ? 280 : 300,
                  },
                },
              }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="coach">Coach</MenuItem>
              <MenuItem value="parent">Parent</MenuItem>
              <MenuItem value="joueur">Joueur</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl 
            size={isMobile ? "medium" : "small"} 
            sx={{ 
              minWidth: isMobile ? '50%' : 120,
              flex: isMobile ? 1 : 'none'
            }}
          >
            <InputLabel>Statut</InputLabel>
            <Select
              value={filterStatus}
              label="Statut"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ 
                fontSize: isMobile ? '1rem' : 'inherit',
                height: isMobile ? '56px' : 'inherit'
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: isMobile ? 280 : 300,
                  },
                },
              }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="suspended">Suspendu</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Affichage des erreurs */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Affichage des utilisateurs - Tableau sur desktop, cartes sur mobile */}
      {!isMobile ? (
        // Version desktop: tableau classique
        <TableContainer component={Paper} sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }} aria-label="tableau des utilisateurs">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell>Email</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Date d'inscription</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.displayName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          color={
                            user.role === 'admin' ? 'error' :
                            user.role === 'coach' ? 'primary' :
                            user.role === 'parent' ? 'success' : 'default'
                          }
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status === 'active' ? 'Actif' : 'Suspendu'}
                          size="small"
                          color={user.status === 'active' ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(user.createdAt)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.status === 'active' ? 'Suspendre' : 'Activer'}>
                          <IconButton
                            size="small"
                            color={user.status === 'active' ? 'warning' : 'success'}
                            onClick={() => handleToggleUserStatus(user)}
                          >
                            {user.status === 'active' ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
onClick={() => handleOpenDeleteDialog(user)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Version mobile: cartes
        <Box sx={{ mb: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100px">
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Paper className="p-4 text-center" sx={{ borderRadius: '12px' }}>
              Aucun utilisateur trouvé
            </Paper>
          ) : (
            <Box className="space-y-3">
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <Card key={user.id} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1" component="div" fontWeight="bold" className="break-all">
                          {user.email}
                        </Typography>
                        <Box>
                          <Chip
                            label={user.role}
                            size="small"
                            color={
                              user.role === 'admin' ? 'error' :
                              user.role === 'coach' ? 'primary' :
                              user.role === 'parent' ? 'success' : 'default'
                            }
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          />
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" className="mr-2">
                          Nom:
                        </Typography>
                        <Typography variant="body1">
                          {user.displayName || '-'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" className="mr-2">
                          Statut:
                        </Typography>
                        <Chip
                          label={user.status === 'active' ? 'Actif' : 'Suspendu'}
                          size="small"
                          color={user.status === 'active' ? 'success' : 'warning'}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" className="mr-2">
                          Inscrit le:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(user.createdAt)}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <Divider />
                    
                    <CardActions sx={{ justifyContent: 'space-between', py: 1 }}>
                      <Button
                        startIcon={<EditIcon />}
                        size="small"
                        onClick={() => handleOpenEditDialog(user)}
                        sx={{ textTransform: 'none' }}
                      >
                        Modifier
                      </Button>
                      
                      <Box>
                        <Button
                          startIcon={user.status === 'active' ? <BlockIcon /> : <CheckCircleIcon />}
                          size="small"
                          color={user.status === 'active' ? 'warning' : 'success'}
                          onClick={() => handleToggleUserStatus(user)}
                          sx={{ textTransform: 'none', mr: 1 }}
                        >
                          {user.status === 'active' ? 'Suspendre' : 'Activer'}
                        </Button>
                        
                        <Button
                          startIcon={<DeleteIcon />}
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(user)}
                          sx={{ textTransform: 'none' }}
                        >
                          Supprimer
                        </Button>
                      </Box>
                    </CardActions>
                  </Card>
                ))}
            </Box>
          )}
        </Box>
      )}

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? "" : "Lignes par page:"}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        sx={{
          '.MuiTablePagination-selectLabel': {
            display: isMobile ? 'none' : 'block',
          },
          '.MuiTablePagination-select': {
            marginRight: isMobile ? 0 : 'auto',
          },
          '.MuiTablePagination-actions': {
            marginLeft: isMobile ? 1 : 2,
          }
        }}
      />

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer l'utilisateur {selectedUser?.email} ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'édition d'utilisateur */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
      >
        <DialogTitle>Modifier l'utilisateur</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1">
              Email: <strong>{selectedUser?.email}</strong>
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Rôle</InputLabel>
              <Select
                value={editedRole}
                label="Rôle"
                onChange={(e) => setEditedRole(e.target.value as UserRole)}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="parent">Parent</MenuItem>
                <MenuItem value="joueur">Joueur</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={editedStatus}
                label="Statut"
                onChange={(e) => setEditedStatus(e.target.value as 'active' | 'suspended')}
              >
                <MenuItem value="active">Actif</MenuItem>
                <MenuItem value="suspended">Suspendu</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annuler</Button>
          <Button onClick={handleUpdateUser} color="primary" variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
